import { useRef, useState } from "react";
import { NAV_MAX, NAV_MIN, setNavWidth, useRemember } from "@shared/lib/remember";

/**
 * Ручка ширины левой колонки.
 *
 * Своя, а не `resizable` из shadcn, и вот почему. Сама библиотека под ним
 * (`react-resizable-panels`) умеет больше, чем принято думать: и пиксели, и
 * запоминание, и стрелки на клавиатуре. Дело в раскладке — у неё колонки
 * должны быть её панелями, все сразу. У нас же в ряду до четырёх колонок,
 * и три из них появляются и исчезают по месту: ветка, участники, профиль.
 * Ради одного шва пришлось бы завернуть их все в общую панель — то есть
 * добавить уровень вложенности и зависимость туда, где хватает одного
 * указателя.
 *
 * Второе: на узком экране колонка занимает ширину целиком, а панель ставит
 * свой размер разметкой прямо на узле — правило из таблицы стилей такому
 * проигрывает. Здесь ширина — переменная, и правило для узкого экрана её
 * перекрывает как обычно.
 *
 * Захват указателя, а не слушатели на документе: за краем окна и над чужими
 * узлами события всё равно приходят сюда, а очередь Esc и прочее чужое
 * добро остаётся нетронутым.
 */

/** На сколько двигают стрелки. С Shift — заметным шагом. */
const STEP = 8;
const STEP_FAR = 32;

/**
 * Ширина колонки прямо сейчас.
 *
 * Спрашивается у самой колонки, а не берётся из состояния: пока ширину не
 * трогали, её задаёт макет — и знать это число здесь значило бы держать его
 * во второй раз. Заодно так учитывается потолок в долях окна.
 *
 * Слева от шва стоит не колонка, а вся левая сторона — ряд серверов, колонка
 * и своя строка под ними. Поэтому колонку ищем внутри: её ширина, а не
 * ширина стороны, и есть то, что тянут.
 */
function widthOf(grip: HTMLElement): number {
  const panel = grip.previousElementSibling?.querySelector(".panel.nav");
  return panel ? panel.getBoundingClientRect().width : NAV_MIN;
}

export function NavGrip() {
  const width = useRemember((state) => state.width);
  const [held, setHeld] = useState(false);
  const from = useRef({ x: 0, width: 0 });

  return (
    <div
      className={`nav-grip ${held ? "held" : ""}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Ширина колонки"
      aria-valuenow={width ?? undefined}
      aria-valuemin={NAV_MIN}
      aria-valuemax={NAV_MAX}
      tabIndex={0}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        // Иначе тянущийся указатель выделяет текст в соседних колонках.
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        from.current = { x: event.clientX, width: widthOf(event.currentTarget) };
        setHeld(true);
      }}
      onPointerMove={(event) => {
        /*
         * Полоска короткая и стоит там, где указатель: посередине экрана она
         * висела бы в стороне от того места шва, за которое берутся. Пишем
         * прямо в стиль — перерисовывать ради неё компонент незачем.
         */
        const grip = event.currentTarget;
        grip.style.setProperty("--grip-y", `${event.clientY - grip.getBoundingClientRect().top}px`);
        if (!held) return;
        setNavWidth(from.current.width + (event.clientX - from.current.x));
      }}
      /*
       * Отпускают не на `pointerup`: захват снимается сам и на нём, и на
       * отмене, и когда окно увели из-под указателя, — а обратно эти случаи
       * не сходятся. Заодно снимать захват руками нельзя: правая кнопка по
       * шву его не берёт вовсе, и снятие взятого не тем нажатием роняет.
       */
      onLostPointerCapture={() => setHeld(false)}
      // Двойное нажатие возвращает ту ширину, что в макете.
      onDoubleClick={() => setNavWidth(null)}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();

        const step = event.shiftKey ? STEP_FAR : STEP;
        const now = widthOf(event.currentTarget);
        setNavWidth(event.key === "ArrowLeft" ? now - step : now + step);
      }}
    />
  );
}
